import {
  AfterContentChecked,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { format } from 'date-fns';
import { SelectItem } from '@openng/optimus-ui/api';
import {
  firstValueFrom,
  forkJoin,
  interval,
  Observable,
  Subscription,
} from 'rxjs';
import { take } from 'rxjs/operators';
import {
  DELETE_SHIFT_TURNOVER,
  VIEW_AUDIT,
} from 'src/app/constants/action-constants';
import {
  CRM_ST_DETAIL_CONTAINER,
  CRM_ST_PRINT_CONTAINER,
  MOC_EVENT_DETAILS_CONTAINER,
} from 'src/app/constants/common.constants';
import {
  DEPARTING_COMPLETED,
  INCOMING_COMPLETED,
  NEW_CRM_SHIFT_TURNOVER,
  PRINT_SHIFT_TURNOVER,
} from 'src/app/constants/crm-constants';
import { DetailsContainer } from 'src/app/core/containers/details-container';
import { getDirtyValues } from 'src/app/core/functions/common-functions';
import { Column } from 'src/app/model/common/column';
import { Contact } from 'src/app/model/contacts/contact';
import { ControllerNote } from 'src/app/model/controllerNotes/controllerNote';
import { MOCEvent } from 'src/app/model/crm/moc-event';
import {
  ShiftTurnover,
  UpdateShiftTurnoverInput,
} from 'src/app/model/shift/shift-turnover';
import { AuthService } from 'src/app/services/auth.service';
import { ControllerNotesApiService } from 'src/app/services/api/controller-notes-api.service';
import { CrmShiftTurnoverTableService } from 'src/app/services/table/crm-shift-turnover-table.service';
import { EnvironmentService } from 'src/app/services/environment.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { MocEventApiService } from 'src/app/services/api/moc-event-api.service';
import { MocEventBuilderService } from 'src/app/services/builder/moc-event-builder.service';
import { MocEventTableService } from 'src/app/services/table/moc-event-table.service';
import { ShiftTurnoverApiService } from 'src/app/services/api/shift-turnover-api.service';
import { ShiftTurnoverBuilderService } from 'src/app/services/builder/shift-turnover-builder.service';
import { controllerNoteTurnoverColumns } from '../../definitions/controller-note-turnover-definition';
import { getMOCEventsForAORTableDefinition } from '../../definitions/moc-event-for-aor-table-definition';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import {
  emptyNote,
  isNullOrUndefined,
  sortByProperty,
  SortDirection,
} from 'src/app/utils/utils';
import { DopApiService } from 'src/app/services/api/dop-api.service';
import { Select, SelectChangeEvent } from '@openng/optimus-ui/select';
import { DOPEntry } from 'src/app/model/crm/daily-operating-plan/dop-entry';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { AOR } from 'src/app/model/locations/aor';
import {
  AutoCompleteCompleteEvent,
  AutoComplete,
} from '@openng/optimus-ui/autocomplete';
import { NoteFormComponent } from '../controller-notes/note-form/note-form.component';
import {
  DialogService,
  DynamicDialogRef,
} from '@openng/optimus-ui/dynamicdialog';
import { ControllerNotesBuilderService } from 'src/app/services/builder/controller-notes-builder.service';
import { AorApiService } from 'src/app/services/api/aor-api.service';
import { AuditDialogContainerComponent } from 'src/app/core/containers/audit-dialog-container.component';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { DatePicker } from '@openng/optimus-ui/datepicker';
import { InputText } from '@openng/optimus-ui/inputtext';
import { Ripple } from '@openng/optimus-ui/ripple';
import { ToggleButton } from '@openng/optimus-ui/togglebutton';
import { Textarea } from '@openng/optimus-ui/textarea';
import { InlineValidationComponent } from '../../../core/components/inline-validation.component';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';
import { Dialog } from '@openng/optimus-ui/dialog';
import { ScrollPanel } from '@openng/optimus-ui/scrollpanel';
import { AsyncPipe } from '@angular/common';
import { LogEntryComponent } from 'src/app/core/components/log-entry.component';
import {
  ShiftTurnoverMessageKeys,
  ValidationMessageEntry,
  ValidationParamEntry,
} from 'src/app/core/type-helpers/validation-msg';
import { ShiftGroup } from 'src/app/model/shift/shift-group';
import { BlockableDiv } from 'src/app/common/components/blockable-div/blockable-div.component';

@Component({
  selector: 'app-crm-shift-turnover-details-container',
  templateUrl: './crm-shift-turnover-details-container.component.html',
  styleUrls: ['./crm-shift-turnover-details-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ProgressSpinnerComponent,
    BlockUI,
    FormsModule,
    ReactiveFormsModule,
    Select,
    ButtonDirective,
    DatePicker,
    InputText,
    AutoComplete,
    Ripple,
    ToggleButton,
    Textarea,
    InlineValidationComponent,
    TranslateDirective,
    GeneralGridComponent,
    Dialog,
    ScrollPanel,
    AsyncPipe,
    TranslatePipe,
    LogEntryComponent,
    BlockableDiv,
  ],
})
export class CrmShiftTurnoverDetailsContainerComponent
  extends DetailsContainer
  implements OnInit, OnDestroy, AfterContentChecked
{
  private readonly OPTIMIZATION_AOR_NAME = 'optimization';
  private readonly OPTIMIZATION_AOR_ID = '7e633f74-697d-4025-a826-f515282a15ac';

  protected _fb = inject(UntypedFormBuilder);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _mocApi = inject(MocEventApiService);
  protected _mocEventTableService = inject(MocEventTableService);
  protected _mocBuilder = inject(MocEventBuilderService);
  protected _shiftTurnoverApi = inject(ShiftTurnoverApiService);
  protected _shiftTurnoverTable = inject(CrmShiftTurnoverTableService);
  protected _shiftTurnoverBuilder = inject(ShiftTurnoverBuilderService);
  protected _auth = inject(AuthService);
  protected _notesApi = inject(ControllerNotesApiService);
  protected _env = inject(EnvironmentService);
  protected _authApi = inject(AuthApiService);
  protected _dopApi = inject(DopApiService);
  protected _dialogService = inject(DialogService);
  protected _controllerNoteBuilder = inject(ControllerNotesBuilderService);
  protected _AORApi = inject(AorApiService);

  shiftTurnover: ShiftTurnover | null = null;
  crmDistricts: SelectItem[] = [];
  availableBusinessUnits: SelectItem[] = [];
  totalBusinessUnits: SelectItem[] = [];
  totalAors: any[] = [];
  totalDistricts: SelectItem[] = [];
  isEditting = false;
  filteredContacts: any[] = [];
  addEntryForm!: UntypedFormGroup;
  showEntryDialog = false;
  saving = false;
  actions: SelectItem[] = [];
  startDate: Date = new Date();
  endDate: Date = new Date();
  reviewDateCheck: boolean = false;
  dopEntries: DOPEntry[] = [];

  addIcon = 'fa fa-fw fa-plus';
  updateIcon = 'fa fa-fw fa-save';
  isUnavailable = false;
  deleteMsg!: string;
  okBtnLabel!: string;
  cancelBtnLabel!: string;
  deleteHeader!: string;
  groups: ShiftGroup[] = [];
  shiftTypes: SelectItem[] = [];
  groupName = 'Gas Control Room';
  textAreaSize = 46;
  commentsTextAreaSize = 62;
  deviceSub!: Subscription;
  selectedAction: any;

  validationMessages?: Record<ShiftTurnoverMessageKeys, ValidationMessageEntry>;
  validationParams?: Partial<
    Record<ShiftTurnoverMessageKeys, ValidationParamEntry>
  >;
  canEditUnresolved = false;
  canEditResolved = false;
  canCreate = false;
  previousTurnoverStatus = '';
  canViewDeparting = false;
  canViewIncoming = false;
  showDOPDialog = false;

  mocColumns: Column[] = [];
  unacknowledged: MOCEvent[] = [];
  acknowledgedAndUpcoming: MOCEvent[] = [];
  activeTabChangedSub!: Subscription;
  refreshSubscription?: Subscription;
  backgroundRefreshPaused: boolean = false;
  isOptimizationAor = signal(false);

  turnoverNotes: ControllerNote[] = [];
  noteColumns: Column[] = [];
  noteTotalRecords: number = -1;
  dialogVisible: boolean = false;
  selectedNote: ControllerNote = { ...emptyNote };

  ref: DynamicDialogRef<NoteFormComponent> | null = null;
  dopWorking = signal(false);

  ngOnInit() {
    this.canViewDeparting = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTROL_ROOM.CRM_SHIFT_TURNOVERS
        .VIEW_ALL_CRM_TURNOVER_DEPARTING_SECTIONS,
    ]);
    this.canViewIncoming = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTROL_ROOM.CRM_SHIFT_TURNOVERS
        .VIEW_ALL_CRM_TURNOVER_INCOMING_SECTIONS,
    ]);

    const validations = this._shiftTurnoverBuilder.buildValidationMessages();
    this.validationMessages = validations.messages;
    this.validationParams = validations.params;

    this.setupTabClosingSubscription();
    this.setupActiveTabChangedSubscription();
    const refreshInterval = interval(60000);
    this.refreshSubscription = refreshInterval.subscribe(() => {
      this.performBackgroundRefresh();
    });
    this.initializeShiftTurnover();
  }

  ngAfterContentChecked(): void {
    const activeId = this.id;
    if (
      !isNullOrUndefined(activeId) &&
      this.shiftTurnover &&
      this.shiftTurnover.id !== activeId &&
      !this.doCheckComplete
    ) {
      this.doCheckComplete = true;
      this._shiftTurnoverTable.setSelected(null);
      this.initializeShiftTurnover();
    }
  }

  ngOnDestroy(): void {
    this.deviceSub?.unsubscribe();
    this.tabClosingSub?.unsubscribe();
    this.valueSub?.unsubscribe();
    this.refreshSubscription?.unsubscribe();
  }

  private checkDeviceSize() {
    if (!this._deviceService.isMobile()) {
      this.textAreaSize = 30;
      this.commentsTextAreaSize = 42;
    } else {
      this.textAreaSize = 46;
      this.commentsTextAreaSize = 62;
    }
  }

  initializeShiftTurnover() {
    this.mocColumns = getMOCEventsForAORTableDefinition();
    this.noteColumns = controllerNoteTurnoverColumns;
    this.deviceSub = this._deviceService.mediaChangeEvent.subscribe((event) => {
      this.checkDeviceSize();
    });
    this.checkDeviceSize();

    const id = this.id;
    if (id) {
      this._shiftTurnoverApi.getShiftTurnover(id).subscribe((turnover) => {
        this.doCheckComplete = false;
        this.shiftTurnover = turnover;
        this.updateIsOptimizationAor();
        this.loadShiftData();
      });
    } else {
      this.shiftTurnover = this._shiftTurnoverTable.getSelected();
      this.updateIsOptimizationAor();
      if (this.shiftTurnover) {
        this.isEditting = true;
        // Creating new turnover, we want to default to the current user for departing
        this._contactApi
          .getContactByEmail(this._auth.getEmail()!)
          .pipe(take(1))
          .subscribe((data: Contact) => {
            const id = data.id;
            this.form?.patchValue({
              departingAnalyst: {
                id: id,
                fullName: data.firstName + ' ' + data.lastName,
              },
            });
            this.form?.controls['departingAnalyst'].markAsDirty();
          });
      } else {
        this._logAndMessage.errorLogOnly('CRM Shift Turnover was null');
        const index = this._tabService.getActiveIndex();
        this._tabService.closeTab(index);
      }
      this.loadShiftData();
    }
    this.canEditUnresolved = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTROL_ROOM.CRM_SHIFT_TURNOVERS.EDIT_GC_UNRESOLVED_TURNOVERS,
    ]);
    this.canEditResolved = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTROL_ROOM.CRM_SHIFT_TURNOVERS.EDIT_GC_RESOLVED_TURNOVERS,
    ]);
    this.canCreate = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTROL_ROOM.CRM_SHIFT_TURNOVERS.CREATE_GC_TURNOVERS,
    ]);
  }

  loadShiftData() {
    if (!this.canViewDeparting || !this.canViewIncoming) {
      this._contactApi
        .getContactByEmail(this._auth.getEmail()!)
        .pipe(take(1))
        .subscribe((data: Contact) => {
          const id = data.id;

          this.shiftTurnover?.ShiftDetails.forEach((s) => {
            if (s.incomingDepartingFlag === 'D' && s.analystId === id) {
              this.canViewDeparting = true;
            }

            if (s.incomingDepartingFlag === 'I' && s.analystId === id) {
              this.canViewIncoming = true;
            }
          });
        });
    }

    this._shiftTurnoverApi.getShiftGroups().subscribe((groups) => {
      this.groups = groups;
    });

    this._shiftTurnoverApi.getShiftTypes().subscribe((types) => {
      this.shiftTypes = [
        { label: 'Choose', value: null },
        ...types
          .filter((d) => d.ShiftGroup.name === 'Gas Control Room')
          .map((d) => ({ label: d.name, value: d.id })),
      ];
      this.initialize();
    });

    if (this.shiftTurnover && this.shiftTurnover.id) {
      this.loadControllerNotes();
    }
    if (this.shiftTurnover && this.shiftTurnover.departingCompletedDate) {
      this.loadMocEvents();
    }

    if (this.shiftTurnover?.AOR && this.shiftTurnover.shiftDate) {
      this.loadDOPEntries();
    }
  }

  async initialize() {
    if (isNullOrUndefined(this.shiftTurnover?.id)) {
      const label = await firstValueFrom(
        this._translateService.get('CRM.LABEL.NEW_TURNOVER')
      );
      this._tabService.updateActiveTabLabel(label);
      this.screenName = label;
    } else {
      const header =
        (this.shiftTurnover!.AOR!.abbreviation
          ? this.shiftTurnover!.AOR!.abbreviation
          : this.shiftTurnover!.aor) +
        ' - ' +
        this.shiftTurnover!.shift +
        ' - ' +
        format(this.shiftTurnover!.shiftDate, 'MM/dd/yyyy');
      const tab = this._tabService.getTabById(this.shiftTurnover!.id);
      this._tabService.updateTabLabel(tab!, header);
      this.screenName = header;
    }
    this._AORApi
      .getAORs(null)
      .pipe(take(1))
      .subscribe({
        next: (aors: AOR[]) => {
          this.crmDistricts = [
            { label: 'Choose', value: null },
            ...aors
              .filter(
                (t: AOR) =>
                  t.enabled === true ||
                  (this.shiftTurnover && t.name === this.shiftTurnover.aor)
              )
              .sort((a: AOR, b: AOR) => sortByProperty(a, b, 'name'))
              .map((t: AOR) => ({ label: t.name, value: t.id })),
          ];
          this.buildForm();
          this.updateForm();
          this.totalAors = aors;
          this.totalDistricts = this.crmDistricts;
        },
      });
    this.availableBusinessUnits = this._authApi
      .getUserBusinessUnits()
      .map((bu) => {
        return { label: bu.name, value: bu.id };
      });
    this.totalBusinessUnits = this.availableBusinessUnits;
    this.updateActionsList();
    this.loading = false;
  }

  buildForm() {
    this.form = this._shiftTurnoverBuilder.buildCrmForm(this._fb);
    this.valueSub = this.form.valueChanges.subscribe((changes) => {
      this.updateIsOptimizationAor();
      if (this.form!.dirty) {
        this.changesEvent.emit({
          isDirty: true,
          index: this.index(),
          id: this.id,
        });
      }
    });
  }

  updateForm() {
    this._shiftTurnoverBuilder.buildUpdatedCrmForm(
      this.shiftTurnover!,
      this.form!,
      this._fb
    );
    this.updateIsOptimizationAor();
    this.form?.markAsPristine();

    if (!this.isEditting) {
      this.form?.disable();
    } else {
      this.setEditting();
    }
  }

  aorChange($event: SelectChangeEvent) {
    this.updateIsOptimizationAor();
    if ($event.value !== null) {
      const aor = this.totalAors.find((aor) => aor.id === $event.value);
      this.form?.patchValue({
        businessUnit: aor.businessUnitId,
      });
      this.form?.controls['businessUnit'].markAsDirty();
    } else {
      this.availableBusinessUnits = this.totalBusinessUnits;
    }
  }

  checkAnalystCompleted() {
    if (
      this.shiftTurnover &&
      !this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.CONTROL_ROOM.CRM_SHIFT_TURNOVERS.EDIT_LOCKED_GC_TURNOVERS,
      ])
    ) {
      if (this.shiftTurnover.incomingCompletedDate != null) {
        const groups = (this.form?.controls.items as FormArray).controls;
        groups.forEach((group: AbstractControl<any, any>) => {
          let incomingComments = group.get('incomingComments');
          let incomingTurnoverFlags = group.get('incomingTurnoverFlag');
          incomingComments?.disable();
          incomingTurnoverFlags?.disable();
        });
        this.form!.get('incomingAdditionalComments')?.disable();
        this.form!.get('incomingFitFlag')?.disable();
        this.form!.get('incomingScadaFlag')?.disable();
        this.form?.get('incomingReviewedAllSections')?.disable();
      }

      if (this.shiftTurnover.departingCompletedDate != null) {
        const groups = (this.form?.controls.items as FormArray).controls;
        groups.forEach((group: AbstractControl<any, any>) => {
          let departingComments = group.get('departingComments');
          let departingTurnoverFlags = group.get('departingTurnoverFlag');
          departingComments?.disable();
          departingTurnoverFlags?.disable();
        });
        this.form!.get('mitigations')?.disable();
        this.form!.get('departingAdditionalComments')?.disable();
        this.form!.get('departingFitFlag')?.disable();
        this.form!.get('departingScadaFlag')?.disable();
      }
    }
  }

  async setEditting() {
    if (
      this._authApi.doesUserHaveClaim(
        CLAIMS.CONTROL_ROOM.CRM_SHIFT_TURNOVERS.EDIT_ALL_CRM_TURNOVER_SECTIONS
      )
    ) {
      this.form?.enable();
    } else if (
      this._authApi.doesUserHaveClaim(
        CLAIMS.CONTROL_ROOM.CRM_SHIFT_TURNOVERS.EDIT_OWN_CRM_TURNOVER_SECTIONS
      )
    ) {
      this.form?.enable();
      this.loading = true;
      // determine if the user is either the departing or incoming & only enable those sections
      const contact = await firstValueFrom(
        this._contactApi.getContactByEmail(this._auth.getEmail()!)
      );
      if (!this.shiftTurnover) {
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'CRM.MESSAGES.HEADERS.UNEXPECTED_ERROR',
          bodyKey: 'CRM.MESSAGES.ERROR.UNEXPECTED_ERROR',
        });
        console.error(
          'Somehow user made it to set editting with no shift turnover!'
        );
        return;
      }

      const departingDetail = this.shiftTurnover.ShiftDetails?.filter(
        (sd) => sd.incomingDepartingFlag === 'D'
      )[0];
      const isDeparting = departingDetail?.analystId === contact.id;
      if (
        !isDeparting ||
        (isDeparting && this.shiftTurnover.departingCompletedDate)
      ) {
        console.log('Disabling departing fields');
        console.log('User is departing:', isDeparting);
        console.log(
          'Shift Turnover Departing Completed Date:',
          this.shiftTurnover.departingCompletedDate
        );
        const groups = (this.form!.controls.items as FormArray).controls;
        groups.forEach((group: AbstractControl<any, any>) => {
          let departingComments = group.get('departingComments');
          let departingTurnoverFlags = group.get('departingTurnoverFlag');
          departingComments?.disable();
          departingTurnoverFlags?.disable();
        });
        this.form!.get('mitigations')?.disable();
        this.form!.get('departingAdditionalComments')?.disable();
        this.form!.get('departingFitFlag')?.disable();
        this.form!.get('departingScadaFlag')?.disable();
      }

      const incoming = this.shiftTurnover.ShiftDetails?.filter(
        (sd) => sd.incomingDepartingFlag === 'I'
      )[0];
      const isIncoming = incoming?.analystId === contact.id;
      if (
        !isIncoming ||
        (isIncoming && !this.shiftTurnover.departingCompletedDate)
      ) {
        console.log('Disabling Incoming fields');
        console.log('User is incoming:', isIncoming);
        console.log(
          'Shift Turnover Departing Completed Date:',
          this.shiftTurnover.departingCompletedDate
        );
        const groups = (this.form!.controls.items as FormArray).controls;
        groups.forEach((group: AbstractControl<any, any>) => {
          let incomingComments = group.get('incomingComments');
          let incomingTurnoverFlags = group.get('incomingTurnoverFlag');
          incomingComments?.disable();
          incomingTurnoverFlags?.disable();
        });
        this.form!.get('incomingAdditionalComments')?.disable();
        this.form!.get('incomingFitFlag')?.disable();
        this.form!.get('incomingScadaFlag')?.disable();
        this.form?.get('incomingReviewedAllSections')?.disable();
      }

      this.loading = false;

      // If we haven't actually created the turnover yet we want to make sure not to disable
      // the form otherwise we will prevent all creations
      const isInitialCreate = this.shiftTurnover.ShiftDetails.length === 0;
      const userIsOnThisTurnover = isDeparting || isIncoming;

      if (!userIsOnThisTurnover && !isInitialCreate) {
        this.isEditting = false;
        this.form?.disable();
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'CRM.MESSAGES.HEADERS.NO_TURNOVER_EDIT_PERMISSIONS',
          bodyKey: 'CRM.MESSAGES.ERROR.NO_TURNOVER_EDIT_PERMISSIONS',
        });
        return;
      }
    } else {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'CRM.MESSAGES.HEADERS.NO_TURNOVER_EDIT_PERMISSIONS',
        bodyKey: 'CRM.MESSAGES.ERROR.NO_TURNOVER_EDIT_PERMISSIONS',
      });
      return;
    }

    this.isEditting = true;
    this.checkAnalystCompleted();
    this.form!.controls['status'].disable();
    this.form!.controls['createdDt'].disable();
    this.form!.controls['departingCompleted'].disable();
    this.form!.controls['incomingCompleted'].disable();
  }

  updateActionsList() {
    const selectAction$ = this._translateService.get('COMMON.LABEL.CHOOSE');
    const departingComplete$ = this._translateService.get(
      'CRM.LABEL.DEPARTING_COMPLETE'
    );
    const incomingComplete$ = this._translateService.get(
      'CRM.LABEL.INCOMING_COMPLETE'
    );
    const resolve$ = this._translateService.get('CRM.LABEL.RESOLVE');
    const delete$ = this._translateService.get('CRM.LABEL.DELETE');
    const print$ = this._translateService.get('COMMON.LABEL.PRINT');
    const viewAudit$ = this._translateService.get(
      'COMMON.LABEL.VIEW_AUDIT_INFO'
    );

    let select,
      dc,
      ic,
      resolve,
      del,
      print,
      audit = null;
    forkJoin([
      selectAction$,
      departingComplete$,
      incomingComplete$,
      resolve$,
      delete$,
      print$,
      viewAudit$,
    ])
      .pipe(take(1))
      .subscribe((messages) => {
        select = messages[0];
        dc = messages[1];
        ic = messages[2];
        resolve = messages[3];
        del = messages[4];
        print = messages[5];
        audit = messages[6];

        this.actions = [
          { label: select, value: null },
          { label: print, value: PRINT_SHIFT_TURNOVER },
        ];

        if (this.shiftTurnover!.status === 'N') {
          this.actions.push({ label: dc, value: DEPARTING_COMPLETED });
        }

        if (this.shiftTurnover!.status === 'DC') {
          this.actions.push({ label: ic, value: INCOMING_COMPLETED });
        }

        if (this.canEditUnresolved || this.canEditResolved) {
          this.actions.push({ label: del, value: DELETE_SHIFT_TURNOVER });
        }

        this.actions.push({ label: audit, value: VIEW_AUDIT });

        this.actions = [...this.actions];
        this.selectedAction = null;
      });
  }

  actionEvent($event: SelectChangeEvent, object?: Select) {
    this.selectedAction = null;

    if ($event.value) {
      if ($event.value === DEPARTING_COMPLETED) {
        //######################################################################
        //DEPARTING
        //######################################################################
        this.shiftTurnover!.ShiftDetails.forEach((d) => {
          if (d.incomingDepartingFlag === 'D') {
            let saveMe = true;
            const anyFalseItems = d.ShiftTurnoverItems.some(
              (i) => i.turnoverFlag === false
            );
            if (anyFalseItems === true) {
              this._logAndMessage.translateToErrorAlert({
                headerKey: 'CRM.MESSAGES.HEADERS.DEPARTING_NOT_COMPLETE',
                bodyKey: 'CRM.MESSAGES.ERROR.MISSING_ITEMS',
              });
              saveMe = false;
            }
            const mitigations = d.ShiftTurnoverMitigations
              ? d.ShiftTurnoverMitigations.filter(
                  (m) => m.turnoverFlag === true
                )
              : [];
            if (!d.fitFlag) {
              this._logAndMessage.translateToErrorAlert({
                headerKey: 'CRM.MESSAGES.HEADERS.DEPARTING_NOT_COMPLETE',
                bodyKey: 'CRM.MESSAGES.ERROR.FIT_MISSING',
              });
              saveMe = false;
            }

            if (!d.scadaFlag) {
              this._logAndMessage.translateToErrorAlert({
                headerKey: 'CRM.MESSAGES.HEADERS.DEPARTING_NOT_COMPLETE',
                bodyKey: 'CRM.MESSAGES.ERROR.SCADA_MISSING',
              });
              saveMe = false;
            }

            if (mitigations.length < 1) {
              this._logAndMessage.translateToErrorAlert({
                headerKey: 'CRM.MESSAGES.HEADERS.DEPARTING_NOT_COMPLETE',
                bodyKey: 'CRM.MESSAGES.ERROR.CRM_MIT_MISSING',
              });
              saveMe = false;
            }
            if (saveMe === true) {
              this.previousTurnoverStatus = this.shiftTurnover!.status;
              this.shiftTurnover!.status = 'DC';
              this.saveShiftTurnover();
            }
          }
        });
      }
      //######################################################################
      //INCOMING COMPLETED
      //######################################################################
      else if ($event.value === INCOMING_COMPLETED) {
        this.handleIncomingComplete(object!);
      }
      //######################################################################
      //PRINT SHIFT TURNOVER
      //######################################################################
      else if ($event.value === PRINT_SHIFT_TURNOVER) {
        const tab = this._tabService.buildNewTab(
          CRM_ST_PRINT_CONTAINER,
          true,
          undefined,
          this.shiftTurnover!.id
        );
        this._tabService.openTab(tab);
      }
      //######################################################################
      //VIEW AUDIT
      //######################################################################
      else if ($event.value === VIEW_AUDIT) {
        const history$ = this._shiftTurnoverApi.getShiftTurnoverHistories(
          this.shiftTurnover!.id
        );
        this._dialogService.open(AuditDialogContainerComponent, {
          dismissableMask: true,
          modal: true,
          closable: true,
          width: '25%',
          data: {
            history: history$,
          },
        });
      }
      //######################################################################
      //DELETE SHIFT TURNOVER
      //######################################################################
      else if ($event.value === DELETE_SHIFT_TURNOVER) {
        if (
          (this.shiftTurnover!.status !== 'R' && !this.canEditUnresolved) ||
          (this.shiftTurnover!.status == 'R' && !this.canEditResolved)
        ) {
          return;
        }

        let confHeader = null,
          ok = null,
          cancel = null,
          message = null;
        const confHeader$ = this._translateService.get(
          'CRM.MESSAGES.HEADERS.DELETE_TURNOVER'
        );
        const ok$ = this._translateService.get('COMMON.LABEL.BUTTONS.YES');
        const cancel$ = this._translateService.get('COMMON.LABEL.BUTTONS.NO');
        const message$ = this._translateService.get(
          'CRM.MESSAGES.CONFIRMATION.DELETE_TURNOVER'
        );

        forkJoin([confHeader$, ok$, cancel$, message$]).subscribe(
          (messages) => {
            confHeader = messages[0];
            ok = messages[1];
            cancel = messages[2];
            message = messages[3];
          }
        );
        this._confirmationService.confirm({
          header: confHeader!,
          icon: 'fa fa-question-circle',
          acceptVisible: true,
          rejectVisible: true,
          acceptLabel: ok!,
          rejectLabel: cancel!,
          message: message!,
          accept: () => {
            this.saving = true;
            this._shiftTurnoverApi
              .deleteShiftTurnover(this.shiftTurnover!.id)
              .subscribe({
                next: (result) => {
                  this.saving = false;
                  this._logAndMessage.translateToSuccessMessage({
                    bodyKey: 'COMMON.MESSAGES.SUCCESS.DELETE_SUCCESS_MSG',
                    headerKey: 'COMMON.MESSAGES.HEADERS.DELETE',
                  });
                  const index = this._tabService.getActiveIndex();
                  this._tabService.closeTab(index);
                },
                error: (error) => {
                  this.saving = false;
                  this._logAndMessage.translateToErrorAlert({
                    bodyKey: 'COMMON.MESSAGES.ERROR.DELETE_ERROR_MSG',
                    headerKey: 'COMMON.MESSAGES.HEADERS.DELETE',
                  });
                },
              });
          },
          reject: () => {},
        });
      }

      if (object) {
        object.clear();
      }
    }
  }

  contactSearch($event: AutoCompleteCompleteEvent) {
    const query = $event && $event.query ? $event.query.toLowerCase() : '';
    this._contactApi
      .getTurnoverContacts(this.shiftTurnover!.shiftGroupId)
      .subscribe((contacts) => {
        this.filteredContacts = contacts
          .sort((a, b) => sortByProperty(a, b, 'firstName'))
          .filter((c) =>
            (c.firstName + ' ' + c.lastName).toLowerCase().includes(query)
          )
          .map((c) => ({
            id: c.id,
            fullName: c.firstName + ' ' + c.lastName,
          }));
      });
  }

  saveShiftTurnover() {
    if (!this.canCreate && !this.canEditResolved && !this.canEditUnresolved) {
      return;
    }
    const dv = getDirtyValues(this.form);
    this.saving = true;
    this.isEditting = false;

    let dtShiftDate = new Date(this.shiftTurnover!.shiftDate);
    //note (MCA): getMonth() is 0 indexed so 1 needs added to it to get the numeric value for the month needed
    let strShiftDate =
      dtShiftDate.getMonth() +
      1 +
      '/' +
      dtShiftDate.getDate() +
      '/' +
      dtShiftDate.getFullYear();
    let dtCurrentDate = new Date();
    let strIncomingCompletedDate =
      dtCurrentDate.getMonth() +
      1 +
      '/' +
      dtCurrentDate.getDate() +
      '/' +
      dtCurrentDate.getFullYear();

    if (strShiftDate != strIncomingCompletedDate) {
      this._logAndMessage.translateToWarnMessage({
        headerKey: 'CRM.MESSAGES.HEADERS.INCOMING_SHIFT_DATE',
        bodyKey: 'CRM.MESSAGES.INFO.INCOMING_SHIFT_DATE_WARNING',
      });
    }

    if (this.shiftTurnover && this.shiftTurnover.id) {
      const updateStatement = this._shiftTurnoverBuilder.buildUpdateStatement(
        this.shiftTurnover,
        dv
      );
      this.updateShiftTurnover(updateStatement);
    } else {
      const createStatement = this._shiftTurnoverBuilder.buildCreateStatement(
        this.shiftTurnover,
        dv
      );
      this.createShiftTurnover(createStatement);
    }
  }

  updateShiftTurnover(updateStatement: UpdateShiftTurnoverInput) {
    if (
      (this.previousTurnoverStatus !== 'R' && !this.canEditUnresolved) ||
      (this.previousTurnoverStatus === 'R' && !this.canEditResolved)
    ) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'CRM.MESSAGES.HEADERS.UPDATE_TURNOVER',
        bodyKey: 'CRM.MESSAGES.ERROR.UPDATE_TURNOVER_PERMS',
      });
      this._logAndMessage.errorLogOnly(
        `User doesn't have permissions to update this turnover, previous status: ${this.previousTurnoverStatus}, canEditUnresolved: ${this.canEditUnresolved}, canEditResolved: ${this.canEditResolved}, updateStatement: ${updateStatement}`
      );
      this.saving = false;
      return;
    }

    this._shiftTurnoverApi
      .updateShiftTurnover(this.shiftTurnover!.id, updateStatement)
      .pipe(take(1))
      .subscribe({
        next: (turnover) => {
          this.previousTurnoverStatus = '';
          this.handleShiftTurnoverSave(turnover);
        },
        error: (error) => {
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'CRM.MESSAGES.HEADERS.UPDATE_TURNOVER',
            bodyKey: 'CRM.MESSAGES.ERROR.UPDATE_TURNOVER',
          });
          console.error(error);
          this.saving = false;
          this.isEditting = true;
        },
      });
  }

  createShiftTurnover(updateStatement: any) {
    if (!this.canCreate) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'CRM.MESSAGES.HEADERS.CREATE_TURNOVER',
        bodyKey: 'CRM.MESSAGES.ERROR.CREATE_TURNOVER_PERMS',
      });
      this._logAndMessage.errorLogOnly(
        "User doesn't have permissions to create turnovers"
      );
      this.saving = false;
      return;
    }

    this._shiftTurnoverApi.createShiftTurnover(updateStatement).subscribe({
      next: (turnover) => {
        this.handleShiftTurnoverSave(turnover);
      },
      error: (error) => {
        console.error(error);
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          bodyKey: 'CRM.MESSAGES.ERROR.CREATE_SHIFT_TURNOVER',
        });
        this.saving = false;
      },
      complete: () => {
        const oldTab = this._tabService.getActiveTab();
        const tab = this._tabService.buildNewTab(
          CRM_ST_DETAIL_CONTAINER,
          true,
          undefined,
          this.shiftTurnover!.id
        );
        this._tabService.replaceTab(oldTab, tab);
      },
    });
  }

  handleShiftTurnoverSave(turnover: ShiftTurnover) {
    this._logAndMessage.translateToSuccessMessage({
      bodyKey: 'COMMON.MESSAGES.SUCCESS.SAVED_SUCCESSFULLY',
      headerKey: 'COMMON.MESSAGES.HEADERS.SUBMIT',
    });
    this.shiftTurnover = turnover;
    this.saving = false;
    this.updateForm();
    this.form?.disable();
    this.updateActionsList();
    this.changesEvent.emit({
      isDirty: false,
      index: this.index(),
      id: this.id,
    });
    if (this.shiftTurnover && this.shiftTurnover.id) {
      this.loadControllerNotes();
    }
    if (this.shiftTurnover && this.shiftTurnover.departingCompletedDate) {
      this.loadMocEvents();
    }
  }

  shouldShowEditSaveButtons() {
    if (
      (this.shiftTurnover!.status !== 'R' && this.canEditUnresolved) ||
      (this.shiftTurnover!.status == 'R' && this.canEditResolved)
    ) {
      return true;
    }

    return false;
  }

  refresh(): void {
    if (this.shiftTurnover && this.shiftTurnover.id) {
      this.saving = true;
      this._shiftTurnoverApi.getShiftTurnover(this.shiftTurnover.id).subscribe(
        (turnover) => {
          this.updatePageTurnover(turnover);
        },
        (error) => {
          this.saving = false;
        },
        () => {
          this.saving = false;
        }
      );
    }
  }

  loadMocEvents(): void {
    if (this.shiftTurnover && this.shiftTurnover.AOR) {
      this._mocApi
        .getMOCEventsForAOR(
          this.shiftTurnover.AOR.id,
          this.shiftTurnover.incomingCompletedDate!
        )
        .subscribe({
          next: (mocs) => {
            if (this.shiftTurnover && this.shiftTurnover.ShiftDetails) {
              const inArray = this.shiftTurnover.ShiftDetails.filter(
                (d) => d.incomingDepartingFlag === 'I'
              );
              if (isNullOrUndefined(this.shiftTurnover.incomingCompletedDate)) {
                const mocEvents = this._mocBuilder.buildMocEventsForAOR(
                  mocs,
                  inArray[0].Analyst!.id,
                  new Date(this.shiftTurnover.shiftDate)
                );
                this.unacknowledged = [...mocEvents.unacknowledged];
                this.acknowledgedAndUpcoming = [...mocEvents.acknowledged];
              } else {
                this.acknowledgedAndUpcoming =
                  this._mocBuilder.buildHistoricalAcknowledgements(
                    mocs,
                    new Date(this.shiftTurnover.incomingCompletedDate!)
                  );
              }
            }
          },
          error: (error) => {
            this._logAndMessage.errorLogOnly(error);
            this._logAndMessage.translateToErrorAlert({
              bodyKey: 'CRM.MESSAGES.ERROR.GET_MOC_EVENTS',
              headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            });
          },
        });
    }
  }

  goToMocEvent($event: { data: MOCEvent }): void {
    if (!isNullOrUndefined($event) && !isNullOrUndefined($event.data)) {
      this._mocEventTableService.setSelected(null);
      const header = $event.data.title.endsWith(' - V' + $event.data.historyId)
        ? $event.data.title
        : null;
      const tab = this._tabService.buildNewTab(
        MOC_EVENT_DETAILS_CONTAINER,
        true,
        header!,
        $event.data.id
      );
      this._tabService.openTab(tab);
    }
  }

  setupActiveTabChangedSubscription(): void {
    this.activeTabChangedSub = this._tabService.activeTabChanged.subscribe(
      (tab) => {
        if (!isNullOrUndefined(tab) && tab.header === this.screenName) {
          this.loadMocEvents();
          this.loadControllerNotes();

          if (!this.isEditting) {
            this.refresh();
          }
        }
      }
    );
  }

  loadControllerNotes() {
    // If the shift date of the turnover is before this hardcoded date, then empty
    // out the data and return
    // This is to avoid any potential issues where notes could have been shown that
    // were unavailable to the controller at the time of the turnover being closed
    if (this.shiftTurnover!.shiftDate < this._env.getNoTurnoverNotesBefore()) {
      console.log(
        'This shift turnover is from before the update! Not pulling controller notes!'
      );
      this.turnoverNotes = [];
      this.noteTotalRecords = 0;
      return;
    }

    if (this.shiftTurnover!.incomingCompletedDate) {
      // If we have the incoming completed, then the notes should be migrated
      // so we need to grab them from there!
      this._notesApi
        .getClosedShiftTurnoverNotes(this.shiftTurnover!.id)
        .pipe(take(1))
        .subscribe({
          next: (notes) => {
            this.turnoverNotes = notes;
            this.noteTotalRecords = this.turnoverNotes.length;
          },
          error: (error) => {
            this._logAndMessage.errorAlert(
              `Error loading closed turnover notes! ${error.message}`
            );
          },
        });
    } else {
      // We only want to show controller notes to incoming controllers,
      // so if the departingCompletedDate isn't filled out yet, make sure the
      // table is empty and return
      if (!this.shiftTurnover?.departingCompletedDate) {
        this.turnoverNotes = [];
        this.noteTotalRecords = 0;
        return;
      }
      // Filter the controller notes based on the departing completed date because
      // GC West makes their turnovers 12 hours ahead of the actual turnover, so
      // this ensures we get the more accurate data based on the departingCompletedDate
      // when the incoming controller needs to review the turnover

      // Start date should be exactly 24 hours before the end date, and
      // the end date should be exactly the departingCompletedDate
      let startDate = new Date(this.shiftTurnover.departingCompletedDate);
      startDate.setHours(startDate.getHours() - 24);
      let endDate = new Date(this.shiftTurnover.departingCompletedDate);
      this._notesApi
        .getControllerNotesForTurnover(
          startDate,
          endDate,
          this.shiftTurnover.aor!
        )
        .pipe(take(1))
        .subscribe({
          next: (controllerNote) => {
            this.turnoverNotes = controllerNote;
            this.noteTotalRecords = this.turnoverNotes?.length;
          },
          error: (error) => {
            this._logAndMessage.errorLogOnly(error);
            this._logAndMessage.translateToErrorAlert({
              headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
              bodyKey: 'CRM.MESSAGES.HEADERS.ERROR.QUERY_CONTROLLER_NOTES',
            });
          },
        });
    }
  }

  async openControllerNote($event: { data: ControllerNote }): Promise<void> {
    this.selectedNote = $event.data;

    const dialogHeader = await firstValueFrom(
      this._translateService.get('CRM.MESSAGES.HEADERS.NOTE_INFORMATION')
    );
    this.ref = this._dialogService.open(NoteFormComponent, {
      header: dialogHeader,
      dismissableMask: true,
      modal: true,
      closable: true,
      width: '80%',
      data: {
        controllerNote: this.selectedNote,
        formType: 'Edit',
        aors: [this.shiftTurnover?.AOR],
        category: this.selectedNote.category,
        viewOnly: true,
      },
    });
    this.ref?.onClose.subscribe((data) => {
      if (!data) {
        // If there's no data provided in the onclose, we didn't actually do anything to the
        // so we don't need to refresh the page
        return;
      }
      this.refresh();
    });
  }

  onDialogHide() {
    this.selectedNote = { ...emptyNote };
    this.dialogVisible = false;
  }

  getChildControls(key: string): AbstractControl[] {
    const keyArray = this.form?.get(key) as FormArray;
    return keyArray.controls;
  }

  // Returns a date string in the format YYYYMMDD
  getShiftDateString(sd: Date): string {
    return sd.toISOString().slice(0, 10).replace(/-/g, '');
  }

  async handleIncomingComplete(object: Select) {
    this.saving = true;
    let dtShiftDate = new Date(this.shiftTurnover!.shiftDate);
    //note (MCA): getMonth() is 0 indexed so 1 needs added to it to get the numeric value for the month needed
    let strShiftDate =
      dtShiftDate.getMonth() +
      1 +
      '/' +
      dtShiftDate.getDate() +
      '/' +
      dtShiftDate.getFullYear();
    let dtCurrentDate = new Date();
    let strIncomingCompletedDate =
      dtCurrentDate.getMonth() +
      1 +
      '/' +
      dtCurrentDate.getDate() +
      '/' +
      dtCurrentDate.getFullYear();

    if (strShiftDate != strIncomingCompletedDate) {
      this._logAndMessage.translateToWarnMessage({
        headerKey: 'CRM.MESSAGES.HEADERS.INCOMING_SHIFT_DATE',
        bodyKey: 'CRM.MESSAGES.INFO.INCOMING_SHIFT_DATE_WARNING',
      });
    }

    if (this.unacknowledged.length > 0) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        bodyKey: 'CRM.MESSAGES.ERROR.UNACKNOWLEDGED_MOC_EVENTS_EXIST',
      });
      if (!isNullOrUndefined(object)) {
        object.clear();
      }
      this.saving = false;
      return;
    }

    const incomingDetails = this.shiftTurnover!.ShiftDetails.find(
      (details) => details.incomingDepartingFlag === 'I'
    );
    const secondaryIncomingDetails = this.shiftTurnover!.ShiftDetails.find(
      (details) => details.incomingDepartingFlag === 'S'
    );

    if (!incomingDetails) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        bodyKey: 'CRM.MESSAGES.ERROR.INCOMING_DETAILS_NOT_FOUND',
      });
      if (!isNullOrUndefined(object)) {
        object.clear();
      }
      this.saving = false;
      return;
    }

    let saveMe = true;
    const anyFalseItems = incomingDetails.ShiftTurnoverItems.some(
      (i) => i.turnoverFlag === false
    );
    if (anyFalseItems === true) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'CRM.MESSAGES.HEADERS.INCOMING_NOT_COMPLETE',
        bodyKey: 'CRM.MESSAGES.ERROR.MISSING_ITEMS',
      });
      saveMe = false;
    }

    if (!incomingDetails.fitFlag) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'CRM.MESSAGES.HEADERS.INCOMING_NOT_COMPLETE',
        bodyKey: 'CRM.MESSAGES.ERROR.CRM_FIT_MISSING',
      });
      saveMe = false;
    }

    if (!incomingDetails.scadaFlag) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'CRM.MESSAGES.HEADERS.INCOMING_NOT_COMPLETE',
        bodyKey: 'CRM.MESSAGES.ERROR.SCADA_MISSING',
      });
      saveMe = false;
    }

    if (!incomingDetails.reviewedAllSections) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'CRM.MESSAGES.HEADERS.INCOMING_NOT_COMPLETE',
        bodyKey: 'CRM.MESSAGES.ERROR.REVIEWED_ALL_SECTIONS',
      });
      saveMe = false;
    }

    // Only need to check for these being complete if we have a secondary incoming controller on the turnover
    if (
      secondaryIncomingDetails &&
      this.form?.get('secondaryIncomingAnalyst')?.value
    ) {
      if (!secondaryIncomingDetails.fitFlag) {
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'CRM.MESSAGES.HEADERS.SECONDARY_INCOMING_NOT_COMPLETE',
          bodyKey: 'CRM.MESSAGES.ERROR.CRM_FIT_MISSING',
        });
        saveMe = false;
      }

      if (!secondaryIncomingDetails.scadaFlag) {
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'CRM.MESSAGES.HEADERS.SECONDARY_INCOMING_NOT_COMPLETE',
          bodyKey: 'CRM.MESSAGES.ERROR.SCADA_MISSING',
        });
        saveMe = false;
      }

      if (!secondaryIncomingDetails.reviewedAllSections) {
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'CRM.MESSAGES.HEADERS.SECONDARY_INCOMING_NOT_COMPLETE',
          bodyKey: 'CRM.MESSAGES.ERROR.REVIEWED_ALL_SECTIONS',
        });
        saveMe = false;
      }
    }

    const acknowledgeResult = await firstValueFrom(
      this._dopApi.hasControllerAcknowledgedDOP(
        this.shiftTurnover!.AOR!.id,
        incomingDetails.analystId!,
        this.getShiftDateString(this.shiftTurnover!.shiftDate)
      )
    );

    if (!acknowledgeResult) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'CRM.MESSAGES.HEADERS.INCOMING_NOT_COMPLETE',
        bodyKey: 'CRM.MESSAGES.ERROR.DOP_NOT_ACKNOWLEDGED',
      });
      saveMe = false;
      this.saving = false;
    }

    if (saveMe === true) {
      this.previousTurnoverStatus = this.shiftTurnover!.status;
      this.shiftTurnover!.status = 'R';
      this.saveShiftTurnover();
      this._notesApi
        .migrateShiftTurnoverNotes(this.shiftTurnover!.id)
        .subscribe({
          next: (result) => {
            // After migrating the notes, we need to re-load them so we make sure they
            // are coming from the new source
            this.loadControllerNotes();
          },
          error: (error) => {
            this._logAndMessage.errorAlert(
              `Error while migrating controller notes for turnover ${error.message}`
            );
          },
        });
    } else {
      this.saving = false;
    }
  }

  loadDOPEntries(): void {
    this._dopApi
      .getDOPEntries(
        this.shiftTurnover!.AOR!.dopGroupId!,
        this.getShiftDateString(this.shiftTurnover!.shiftDate)
      )
      .subscribe({
        next: (dopEntries: DOPEntry[]): any => {
          this.dopEntries = dopEntries.sort((a, b) =>
            sortByProperty(a, b, 'displayDate', SortDirection.DESCENDING)
          );
          // this.isLoading = false;
        },
        error: (error) => console.error(error),
      });
  }

  getMatchingEntriesForAor(aorId: string | undefined): DOPEntry[] {
    if (this.dopEntries) {
      return this.dopEntries.filter(
        (entry) => entry.aorId === aorId && !entry.aorSubSystemId
      );
    }

    return [];
  }

  getMatchingEntriesForAorSubSystem(subsystemId: string): DOPEntry[] {
    if (this.dopEntries) {
      return this.dopEntries.filter(
        (entry) => entry.aorSubSystemId === subsystemId
      );
    }

    return [];
  }

  onViewDopClick(): void {
    this.showDOPDialog = true;
  }

  getHeaderForDopDialog(): Observable<string> {
    const header = this._translateService.get(
      'DOP.MESSAGES.HEADERS.TURNOVER_DOP_DIALOG',
      {
        aor: this.shiftTurnover?.AOR?.name,
        shiftDate: this.shiftTurnover?.shiftDate.toDateString(),
      }
    );

    return header;
  }

  async onAcknowledgeDopClick(): Promise<any> {
    this.dopWorking.set(true);
    const contactByEmailResponse = await firstValueFrom(
      this._contactApi.getContactByEmail(this._auth.getEmail()!)
    );
    this._dopApi
      .acknowledgeDOP({
        dopGroupId: this.shiftTurnover!.AOR!.dopGroupId!,
        shiftDate: this.getShiftDateString(this.shiftTurnover!.shiftDate),
        contactId: contactByEmailResponse.id,
      })
      .subscribe({
        next: (acknowledgement) => {
          this._logAndMessage.translateToSuccessMessage({
            headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
            bodyKey: 'DOP.MESSAGES.SUCCESS.DOP_ACKNOWLEDGED',
          });
          this.showDOPDialog = false;
          this.dopWorking.set(false);
        },
        error: (error) => {
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey: 'DOP.MESSAGES.ERROR.DOP_ACKNOWLEDGED',
          });
          console.error(error);
          this.showDOPDialog = false;
          this.dopWorking.set(false);
        },
      });
  }

  getBaseFormChild(key: string): FormControl {
    return this.form?.get(key) as FormControl;
  }

  getItemFormChild(groupIndex: number, key: string): FormControl {
    return this.form
      ?.get('items')
      ?.get(`${groupIndex}`)
      ?.get(key) as FormControl;
  }

  updatePageTurnover(turnover: ShiftTurnover): void {
    this.shiftTurnover = turnover;
    this.loadShiftData();
    this.backgroundRefreshPaused = false;
    this._logAndMessage.translateToSuccessMessage({
      headerKey: 'CRM.MESSAGES.HEADERS.BACKGROUND_TURNOVER_UPDATE_SUCCESS',
      bodyKey: 'CRM.MESSAGES.SUCCESS.BACKGROUND_TURNOVER_UPDATE_SUCCESS',
    });
    this.isEditting = false;
  }

  performBackgroundRefresh(): void {
    if (!this.shiftTurnover || this.backgroundRefreshPaused) {
      // No turnover id to refresh so we can skip for now
      // or the refresh is paused due to them declining an update
      return;
    }

    this._shiftTurnoverApi.getShiftTurnover(this.shiftTurnover.id).subscribe({
      next: async (turnover) => {
        const currentPageVersion = this.shiftTurnover!.version;
        const newVersion = turnover.version;

        if (newVersion > currentPageVersion) {
          // Automatically update in the background when they aren't currently editing
          if (!this.isEditting) {
            this.updatePageTurnover(turnover);
          } else {
            // Prompt to ask for update or not
            const translations = await firstValueFrom(
              forkJoin({
                header: this._translateService.get(
                  'CRM.MESSAGES.HEADERS.BACKGROUND_TURNOVER_UPDATE'
                ),
                message: this._translateService.get(
                  'CRM.MESSAGES.CONFIRMATION.BACKGROUND_TURNOVER_UPDATE'
                ),
              })
            );
            this._confirmationService.confirm({
              header: translations.header,
              icon: 'fa fa-question-circle',
              message: translations.message,
              accept: () => {
                this.updatePageTurnover(turnover);
              },
              reject: () => {
                this.backgroundRefreshPaused = true;
              },
            });
          }
        }
      },
      error: (err) => {
        console.warn('Failed background refresh for this turnover', err);
      },
    });
  }

  private updateIsOptimizationAor(): void {
    const currentAorName = this.shiftTurnover?.AOR?.name?.trim().toLowerCase();
    const selectedAorId = this.form?.get('aor')?.value;

    this.isOptimizationAor.set(
      currentAorName === this.OPTIMIZATION_AOR_NAME ||
        selectedAorId === this.OPTIMIZATION_AOR_ID
    );
  }
}
